import * as React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import { observer } from 'mobx-react-lite';
import { useRootStore } from '../../../../../../providers/root-store-provider';
import { routes } from '../../../../../../config/routes';
import { CardDashboardTradesLoading } from '../card-loading/card-dashboard-trades-loading';
import { IconArrowRightWide } from '../../../../icons/arrow-right-wide';
import { IconButton } from '../../../../icon/button/icon-button';
import { CardTitle } from '../../../title/card-title';
import { CardDashboardTradesDividendsContent } from './content/card-dashboard-trades-dividends-content';

type CardDashboardTradesDividendsType = {
    shared?: boolean;
};

export const CardDashboardTradesDividends: React.FC<CardDashboardTradesDividendsType> = observer(
    ({ shared = false }: CardDashboardTradesDividendsType) => {
        const { t } = useTranslation();
        const { statisticStore, portfolioStore } = useRootStore();
        const { overview } = statisticStore.overviewStore;
        const router = useRouter();

        const portfolioCurrency = portfolioStore.selectedPortfolio.portfolioHistoryCurrencyCode;

        if (!overview) {
            return null;
        }

        const getActions = useMemo(() => {
            return (
                <div className="flex items-center gap-xsmall">
                    <IconButton
                        icon={<IconArrowRightWide />}
                        variation={'outlined'}
                        size={'small'}
                        onClick={async () => {
                            await router.push(`${routes.app.dividends}`);
                        }}
                    />
                </div>
            );
        }, []);

        return (
            <CardDashboardTradesLoading
                fields={['dividends', 'dividendsYield', 'dividendsYieldOnCost', 'dividendsNextYearPrediction']}
            >
                <div className="relative flex h-full flex-col justify-between items-stretch rounded-large bg-surface p-[1.6rem] shadow-strong lg:p-[2.2rem] max-md:bg-[url('/images/card-stats/dividends-bg.svg')] max-md:bg-[length:auto_10rem] max-md:bg-[position:100%_100%] max-md:bg-no-repeat">
                    <CardTitle title={t('layout.statisticsCard.dividends.title')} actions={shared || getActions} />
                    <CardDashboardTradesDividendsContent
                        dividendsYield={overview.dividendsYield}
                        dividendsYieldOnCost={overview.dividendsYieldOnCost}
                        dividendsNextYearPrediction={overview.dividendsNextYearPrediction}
                        dividends={overview.dividends}
                        currency={portfolioCurrency}
                        yieldLabel={t('layout.statisticsCard.dividends.yield')}
                        yieldOnCostLabel={t('layout.statisticsCard.dividends.yieldOnCost')}
                        predictionLabel={t('layout.statisticsCard.dividends.prediction')}
                        predictionTooltip={t('layout.statisticsCard.dividends.last12Months')}
                        payoutLabel={t('layout.statisticsCard.dividends.payout')}
                    />
                </div>
            </CardDashboardTradesLoading>
        );
    },
);
